import React from "react"
import clsx from "clsx"
import Image from "next/legacy/image"
import { useRouter } from "next/router"
import pluralize from "pluralize"
import Select, { Theme } from "react-select"
import S from "./colour-variant-selector.module.scss"
import { getProductUrl } from "@/lib/product"
import { titleCase } from "@/lib/utils"
import { Product } from "@/types"

type Props = {
  product: Product
}

const customStyles = {
  // @ts-ignore
  control: (provided) => {
    return {
      ...provided,
      borderColor: "#ededed",
      boxShadow: "none",
      height: "2.5rem",
    }
  },
  indicatorSeparator: () => ({
    display: "none",
  }),
  // @ts-ignore
  valueContainer: (provided) => ({
    ...provided,
    height: "2.5rem",
  }),
  // @ts-ignore
  singleValue: (provided) => ({
    ...provided,
    fontSize: "0.875rem",
  }),
}

const customTheme = (theme: Theme) => ({
  ...theme,
  colors: {
    ...theme.colors,
    primary: "#29667f",
    primary75: "rgba(41, 102, 127, 0.75)",
    primary50: "rgba(41, 102, 127, 0.5)",
    primary25: "rgba(41, 102, 127, 0.25)",
    danger: "#e60003",
  },
})

const DropdownIndicator = ({ ...props }) => {
  return (
    <div
      className={clsx(S.indicator, {
        [S.focused]: props.selectProps.menuIsOpen,
      })}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="6"
        viewBox="0 0 12 6"
      >
        <path
          fill="#29667f"
          d="M10.588.088a.583.583 0 01.886.752l-.062.072-5 5a.583.583 0 01-.752.062l-.072-.062-5-5A.583.583 0 011.34.026l.072.062L6 4.676z"
        />
      </svg>
    </div>
  )
}

const ColourVariantSelector: React.FC<Props> = ({ product }) => {
  const router = useRouter()

  return (
    <>
      <label htmlFor="colour-variant">
        {product.colour_variations.length} colour{" "}
        {pluralize("variant", product.colour_variations.length)}
      </label>
      <Select
        inputId="colour-variant"
        styles={customStyles}
        theme={customTheme}
        isSearchable={false}
        components={{ DropdownIndicator }}
        options={[...product.colour_variations]
          .sort((a, b) =>
            (a.colour?.name || a.description).localeCompare(
              b.colour?.name || b.description,
            ),
          )
          .map((item) => ({
            value: getProductUrl(item),
            label: (
              <div className="d-flex align-items-center text-none">
                {product.image_url && (
                  <div className="mr-2 flex-shrink-0">
                    <Image
                      src={item.image_url}
                      width={20}
                      height={20}
                      className="rounded-circle"
                    />
                  </div>
                )}
                <p className={S.label}>
                  {titleCase(item.colour?.name || item.description)}
                </p>
              </div>
            ),
          }))}
        className={clsx(S.customSelect)}
        value={{
          value: getProductUrl(product),
          label: (
            <div className="d-flex align-items-center text-none">
              {product.image_url && (
                <div className="mr-2 flex-shrink-0">
                  <Image
                    src={product.image_url}
                    width={20}
                    height={20}
                    className="rounded-circle"
                  />
                </div>
              )}
              <p className={S.label}>
                {titleCase(product.colour?.name || product.description)}
              </p>
            </div>
          ),
        }}
        onChange={(option) =>
          router.push(option!.value, undefined, {
            scroll: false,
          })
        }
      />
    </>
  )
}

export default ColourVariantSelector
